import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react'
import { Workbox } from 'workbox-window'
import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { requestPersistentStorage } from './use-indexeddb'
import { CLEAR_NOTIFICATIONS, DELETE_SUBSCRIPTION, STORE_SUBSCRIPTION } from '@/lib/sw-messages'
import { getPushConfigError } from '@/lib/vapid-config'

const applicationServerKey = process.env.NEXT_PUBLIC_VAPID_PUBKEY

const ServiceWorkerContext = createContext()

export { CLEAR_NOTIFICATIONS, DELETE_SUBSCRIPTION, STORE_SUBSCRIPTION }

export const ServiceWorkerProvider = ({ children }) => {
  const [registration, setRegistration] = useState(null)
  const [support, setSupport] = useState({ serviceWorker: undefined, pushManager: undefined })
  const [permission, setPermission] = useState({ notification: undefined })
  const [savePushSubscription] = useMutation(
    gql`
      mutation savePushSubscription(
        $endpoint: String!
        $p256dh: String!
        $auth: String!
      ) {
        savePushSubscription(
          endpoint: $endpoint
          p256dh: $p256dh
          auth: $auth
        ) {
          id
        }
      }
    `)
  const [deletePushSubscription] = useMutation(
    gql`
      mutation deletePushSubscription($endpoint: String!) {
        deletePushSubscription(endpoint: $endpoint) {
          id
        }
      }
    `)

  // I am not entirely sure if this is needed since at least in Brave,
  // using `registration.pushManager.subscribe` also prompts the user.
  // However, I am keeping this here since that's how it's done in most guides.
  // Could be that this is required for the `registration.showNotification` call
  // to work or that some browsers will break without this.
  const requestNotificationPermission = useCallback(() => {
    // https://web.dev/push-notifications-subscribing-a-user/#requesting-permission
    return new Promise(function (resolve, reject) {
      const permission = window.Notification.requestPermission(function (result) {
        resolve(result)
      })
      if (permission) {
        permission.then(resolve, reject)
      }
    }).then(function (permission) {
      setPermission({ notification: permission })
      if (permission === 'granted') return subscribeToPushNotifications()
    })
  })

  // Resolve a live registration from the Workbox state, falling back to the
  // browser's own lookup. Throws a friendly error when none exists — the
  // previous code dereferenced the null state directly, crashing with
  // "can't access property pushManager, registration is null" whenever the
  // service worker had not registered yet (or failed, e.g. on plain http).
  const getRegistration = useCallback(async () => {
    const active = registration ?? await navigator.serviceWorker.getRegistration()
    if (!active) {
      throw new Error('no active service worker found — push notifications are unavailable (service workers need HTTPS or localhost)')
    }
    return active
  }, [registration])

  const subscribeToPushNotifications = async () => {
    const configError = getPushConfigError()
    if (configError) throw new Error(configError)
    // serviceWorker.controller is null on forced refreshes
    // see https://w3c.github.io/ServiceWorker/#navigator-service-worker-controller
    if (!navigator.serviceWorker.controller) {
      throw new Error('no active service worker found. try refreshing page.')
    }
    const active = await getRegistration()
    const subscribeOptions = { userVisibleOnly: true, applicationServerKey }
    // Brave users must enable a flag in brave://settings/privacy first
    // see https://stackoverflow.com/a/69624651
    let pushSubscription = await active.pushManager.subscribe(subscribeOptions)
    const { endpoint } = pushSubscription
    // convert keys from ArrayBuffer to string
    pushSubscription = JSON.parse(JSON.stringify(pushSubscription))
    // Send subscription to service worker to save it so we can use it later during `pushsubscriptionchange`
    // see https://medium.com/@madridserginho/how-to-handle-webpush-api-pushsubscriptionchange-event-in-modern-browsers-6e47840d756f
    navigator.serviceWorker.controller.postMessage({
      action: STORE_SUBSCRIPTION,
      subscription: pushSubscription
    })
    requestPersistentStorage()

    // send subscription to server
    const variables = {
      endpoint,
      p256dh: pushSubscription.keys.p256dh,
      auth: pushSubscription.keys.auth
    }
    await savePushSubscription({ variables })
  }

  const unsubscribeFromPushNotifications = async (subscription) => {
    await subscription.unsubscribe()
    const { endpoint } = subscription
    await deletePushSubscription({ variables: { endpoint } })
    navigator.serviceWorker.controller.postMessage({ action: DELETE_SUBSCRIPTION })
  }

  const togglePushSubscription = useCallback(async () => {
    const active = await getRegistration()
    const pushSubscription = await active.pushManager.getSubscription()
    if (pushSubscription) {
      return await unsubscribeFromPushNotifications(pushSubscription)
    }
    await subscribeToPushNotifications()
  }, [getRegistration])

  useEffect(() => {
    setSupport({
      serviceWorker: 'serviceWorker' in navigator,
      notification: 'Notification' in window,
      pushManager: 'PushManager' in window
    })
    setPermission({ notification: 'Notification' in window ? window.Notification.permission : 'denied' })

    if (!('serviceWorker' in navigator)) {
      return
    }

    // public/sw.js is emitted only by the InjectManifest build step; it is
    // absent under `next dev`, where /sw.js 302-redirects to the 404 page and
    // register() rejects with SecurityError ("The operation is insecure"). Skip
    // registration in dev, and always swallow failures so a missing/inaccessible
    // sw.js never surfaces as an unhandled rejection.
    if (process.env.NODE_ENV !== 'production') {
      return
    }

    const wb = new Workbox('/sw.js', { scope: '/' })
    wb.register()
      .then(registration => {
        setRegistration(registration)
      })
      .catch(err => {
        console.warn('service worker registration failed:', err?.message ?? err)
      })
  }, [])

  const pushConfigured = useMemo(() => !getPushConfigError(), [])

  const contextValue = useMemo(() => ({
    registration,
    support,
    permission,
    pushConfigured,
    requestNotificationPermission,
    togglePushSubscription
  }), [registration, support, permission, pushConfigured, requestNotificationPermission, togglePushSubscription])

  return (
    <ServiceWorkerContext.Provider value={contextValue}>
      {children}
    </ServiceWorkerContext.Provider>
  )
}

export function clearNotifications () {
  return navigator.serviceWorker?.controller?.postMessage({ action: CLEAR_NOTIFICATIONS })
}

export function useServiceWorker () {
  return useContext(ServiceWorkerContext)
}
